/**
 * Cart-level BUY_X_GET_Y discount calculation.
 *
 * Pools all eligible items belonging to the same sale, sorts them by unit price
 * ascending (cheapest first), then gives the cheapest `freeQty` units free.
 *
 * Formula: freeQty = floor(totalUnits / (buyX + 1))
 *
 * @param items  { unitPrice, qty } for every eligible item in the sale group
 * @param buyX   The X in "buy X get 1 free" (= sale.discount_value)
 * @returns      Total monetary discount to subtract from the cart
 */
export function calcBuyXGetYCartDiscount(
  items: Array<{ unitPrice: number; qty: number }>,
  buyX: number,
): number {
  const x = Math.max(1, Math.floor(buyX));
  // Sort cheapest first so free units come from the lowest-priced items
  const sorted = [...items].sort((a, b) => a.unitPrice - b.unitPrice);
  const totalUnits = sorted.reduce((s, i) => s + i.qty, 0);
  const freeQty = Math.floor(totalUnits / (x + 1));
  let discount = 0;
  let remaining = freeQty;
  for (const item of sorted) {
    if (remaining <= 0) break;
    const take = Math.min(item.qty, remaining);
    discount += take * item.unitPrice;
    remaining -= take;
  }
  return discount;
}

/**
 * Given a list of eligible cart items for a single BUY_X_GET_Y sale,
 * returns how many free units each item receives (index-aligned to `items`).
 *
 * Allocation follows the same cheapest-first order used in the discount calc.
 */
export function allocateBuyXGetYFreeUnits(
  items: Array<{ unitPrice: number; qty: number }>,
  buyX: number,
): number[] {
  const x = Math.max(1, Math.floor(buyX));
  const totalUnits = items.reduce((s, i) => s + i.qty, 0);
  const freeQty = Math.floor(totalUnits / (x + 1));

  // Build index-sorted order (cheapest first) without mutating
  const order = items
    .map((item, idx) => ({ idx, unitPrice: item.unitPrice, qty: item.qty }))
    .sort((a, b) => a.unitPrice - b.unitPrice);

  const allocations = new Array<number>(items.length).fill(0);
  let remaining = freeQty;
  for (const entry of order) {
    if (remaining <= 0) break;
    const take = Math.min(entry.qty, remaining);
    allocations[entry.idx] = take;
    remaining -= take;
  }
  return allocations;
}

export const round = (value: number, precision: number = 2): number => {
  const factor = Math.pow(10, precision);
  return Math.round(value * factor) / factor;
};

export const divide = (a: number, b: number, precision: number = 2): number => {
  if (b === 0) {
    throw new Error('Division by zero is not allowed.');
  }
  return parseFloat((a / b).toFixed(precision));
};

export const multiply = (a: number, b: number, precision: number = 2): number => {
  return parseFloat((a * b).toFixed(precision));
};

export const add = (a: number, b: number, precision: number = 2): number => {
  return parseFloat((a + b).toFixed(precision));
};

export const subtract = (a: number, b: number, precision: number = 2): number => {
  return parseFloat((a - b).toFixed(precision));
};
